import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { CreditTransaction } from '../types';
import { formatCurrency } from '../util/formatCurrency';

type Props = {
  page: number;
  onPageChange: (page: number) => void;
};

const CreditTransactionsList: React.FC<Props> = ({ page, onPageChange }) => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTransactions = async (
    search: string = '',
    page: number = 1,
    transactionType: string = '',
    startDate: string = '',
    endDate: string = ''
  ) => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('customer_search', search);
      if (transactionType) query.append('transaction_type', transactionType);
      if (startDate) query.append('start_date', startDate);
      if (endDate) query.append('end_date', endDate);
      query.append('page', String(page));
      query.append('per_page', '10');

      const res = await fetchWithAuth(`/credit_transactions?${query.toString()}`);

      if (!res.ok) {
        throw new Error('Failed to fetch credit transactions');
      }

      const data = await res.json();
      setTransactions(data.credit_transactions);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(searchTerm, page, transactionTypeFilter, startDate, endDate);
  }, [searchTerm, page, transactionTypeFilter, startDate, endDate]);

  const handleSearch = () => {
    onPageChange(1);
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  };

  const getTypeBadge = (transactionType: CreditTransaction['transaction_type']) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    switch (transactionType) {
      case 'credit_added':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Added</span>;
      case 'credit_applied':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Applied</span>;
      case 'credit_reversed':
        return <span className={`${baseClasses} bg-orange-100 text-orange-800`}>Reversed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{transactionType}</span>;
    }
  };

  return (
    <>
      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="transactionType" className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Type
            </label>
            <select
              id="transactionType"
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="credit_added">Credit Added</option>
              <option value="credit_applied">Credit Applied</option>
              <option value="credit_reversed">Credit Reversed</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {totalCount} transaction{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="hidden md:table-cell px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance Before
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance After
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No credit transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="hidden md:table-cell px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(txn.created_at)}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      {txn.customer_id ? (
                        <Link to={`/customers/${txn.customer_id}`} className="hover:underline">
                          <div className="text-sm font-medium text-gray-900">{txn.customer_name}</div>
                          <div className="text-sm text-gray-500">{txn.customer_code}</div>
                        </Link>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{txn.customer_name}</div>
                          <div className="text-sm text-gray-500">{txn.customer_code}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      {getTypeBadge(txn.transaction_type)}
                    </td>
                    <td className="px-2 md:px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {formatCurrency(parseFloat(txn.amount))}
                    </td>
                    <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatCurrency(parseFloat(txn.balance_before))}
                    </td>
                    <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatCurrency(parseFloat(txn.balance_after))}
                    </td>
                    <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {txn.notes || '—'}
                    </td>
                    <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-sm text-gray-500">
                      {txn.created_by ? txn.created_by.replace('@tini-express.com', '') : '—'}
                    </td>
                    <td className="hidden md:table-cell px-2 md:px-6 py-4 text-sm text-gray-500">
                      {txn.order_id ? (
                        <Link to={`/orders/${txn.order_id}`} className="text-blue-600 hover:underline">
                          {txn.order_reference || `#${txn.order_id}`}
                        </Link>
                      ) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{page}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CreditTransactionsList;
